import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { interval, EMPTY, Subscription, Observable, timer } from 'rxjs';
import { switchMap, catchError, filter, takeWhile, tap } from 'rxjs/operators';
import { SharedDataService } from './shared-data.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { LogoutCoordinatorService } from './logout-coordinator.service';
import { WebSocketManagerService } from '../connectivity/services/websocket-manager.service';
import { MessageWebsocketService } from '../connectivity/services/message-websocket.service';
import { ConversationWebsocketService } from '../connectivity/services/conversation-websocket.service';
import { StateWebsocketService } from '../connectivity/services/state-websocket.service';
import { UserEventMessage, ConnectionEvent } from '../connectivity/models';
import { environment } from '../environments/enviroment';

interface SessionInvalidatedPayload {
  event_type?: string;
  type?: string;
  reason?: string;
  session_id?: string;
  sessionId?: string;
  timestamp?: string;
}

/**
 * Servicio para monitorear la validez de la sesión del usuario
 * Utiliza tres estrategias:
 * 1. WebSocket user_event: Escucha eventos 'user_event' enviados por el backend (detección instantánea y precisa)
 * 2. WebSocket connection close: Detecta cuando el backend cierra la conexión con código 1000 (respaldo)
 * 3. Polling: Validaciones periódicas como fallback (cada 60 segundos)
 */
@Injectable({
  providedIn: 'root'
})
export class SessionMonitorService {
  private readonly CHECK_INTERVAL = 60000; // 60 segundos (1 minuto)
  private readonly validateUrl = `${environment.mindxbkUrl}UserValidate/`;
  private pollingSubscription: Subscription | null = null;
  private websocketSubscription: Subscription | null = null;
  private userEventSubscription: Subscription | null = null;
  private connectionEventSubscription: Subscription | null = null;
  private activeWebSocketSessionId: string | null = null;
  private isMonitoring = false;
  private countdownSubscription: Subscription | null = null;
  private isManualLogout = false; // Bandera para prevenir popup en logout manual
  private pollingEndpointUnavailable = false;

  private http = inject(HttpClient);
  private router = inject(Router);
  private sharedDataService = inject(SharedDataService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private logoutCoordinator = inject(LogoutCoordinatorService);
  private websocketManager = inject(WebSocketManagerService);
  private messageWebsocketService = inject(MessageWebsocketService);
  private conversationWebsocketService = inject(ConversationWebsocketService);
  private stateWebsocketService = inject(StateWebsocketService);

  /**
   * Inicia el monitoreo periódico de la sesión
   * Solo se ejecuta si hay un usuario logueado
   */
  startMonitoring(): void {
    if (this.isMonitoring) {
      console.log('SessionMonitor: Ya está en ejecución');
      return;
    }

    const user = this.sharedDataService.getAgent();
    if (!user) {
      console.log('SessionMonitor: No hay usuario logueado, no se inicia monitoreo');
      return;
    }

    console.log('SessionMonitor: Iniciando monitoreo de sesión');
    this.isMonitoring = true;
    this.isManualLogout = false; // Resetear bandera al iniciar monitoreo
    this.activeWebSocketSessionId = null;
    this.pollingEndpointUnavailable = false;

    // Estrategia 1: Escuchar eventos de WebSocket (detección instantánea)
    this.setupWebSocketMonitoring();

    // Estrategia 2: Polling como fallback (validación periódica)
    this.setupPollingMonitoring();
  }

  /**
   * Configura el monitoreo vía WebSocket para detección instantánea
   */
  private setupWebSocketMonitoring(): void {
    console.log('SessionMonitor: Configurando monitoreo WebSocket', {
      hasWebSocketManager: !!this.websocketManager,
      timestamp: new Date().toISOString()
    });

    // Estrategia 1A: Escuchar mensajes directos de session_invalidated
    // El backend envía mensajes con formato: { type: "session_invalidated", reason: "duplicate_login", ... }
    this.websocketSubscription = this.websocketManager.messages$.pipe(
      filter((message: any) => {
        // CRÍTICO: El mensaje viene envuelto por WebSocketConnectionService:
        // { data: {...mensaje_original...}, type: 'session_invalidated', timestamp: ... }
        const payload = (message?.data ?? message) as SessionInvalidatedPayload;
        const eventType = payload?.event_type ?? payload?.type ?? message?.type;
        if (eventType === 'session') {
          this.setActiveWebSocketSessionId(payload);
          return false;
        }

        const isSessionInvalidated = eventType === 'session_invalidated';

        if (isSessionInvalidated && !this.belongsToActiveSession(payload)) {
          console.warn('SessionMonitor: Ignoring session_invalidated for a previous session', {
            eventSessionId: payload.session_id ?? payload.sessionId,
            activeSessionId: this.activeWebSocketSessionId
          });
          return false;
        }

        // Log para debug (solo mensajes relevantes)
        if (message && message.type !== 'heartbeat' && message.type !== 'pong' && message.type !== 'ping') {
          console.log('SessionMonitor: Mensaje recibido:', {
            wrapperType: message.type,
            dataType: message?.data?.type,
            isSessionInvalidated: isSessionInvalidated,
            hasData: !!message.data
          });
        }

        if (isSessionInvalidated) {
          console.warn('SessionMonitor: session_invalidated frame received', {
            wrapperType: message.type,
            reason: payload.reason,
            eventSessionId: payload.session_id ?? payload.sessionId,
            activeWebSocketSessionId: this.activeWebSocketSessionId
          });
          console.warn('SessionMonitor: ¡Mensaje session_invalidated DETECTADO!', {
            wrapperType: message.type,
            dataKeys: message.data ? Object.keys(message.data) : null
          });
        }

        return isSessionInvalidated;
      })
    ).subscribe({
      next: (message: any) => {
        // El mensaje original está en message.data
        const msgData = (message?.data ?? message) as SessionInvalidatedPayload;

        console.warn('SessionMonitor: ¡SESIÓN INVALIDADA! Procesando logout:', {
          type: msgData.event_type ?? msgData.type,
          reason: msgData.reason,
          sessionId: msgData.session_id ?? msgData.sessionId,
          timestamp: msgData.timestamp
        });

        // Activado: Cierre automático de sesión
        this.handleInvalidSession(msgData.reason || 'session_invalidated', 'session_invalidated');
      },
      error: (error) => {
        console.error('SessionMonitor: Error en WebSocket messages subscription:', error);
      }
    });

    // Estrategia 1B: Escuchar eventos user_event (formato alternativo - por si acaso)
    // Este es el formato anterior, lo mantenemos como fallback
    this.userEventSubscription = this.websocketManager.userEvents$.pipe(
      filter((event: UserEventMessage) => {
        const currentUser = this.sharedDataService.getAgent();
        const shouldProcess = !!(currentUser &&
                  event.agent_id === currentUser.id &&
                  (event.event === 'logout' || event.event === 'session_invalidated' || event.event === 'force_logout'));

        if (shouldProcess) {
          console.log('🔍 SessionMonitor: Evento user_event detectado:', {
            hasCurrentUser: !!currentUser,
            eventAgentId: event.agent_id,
            currentUserId: currentUser?.id,
            eventType: event.event
          });
        }

        return shouldProcess;
      })
    ).subscribe({
      next: (event: UserEventMessage) => {
        console.warn('SessionMonitor: Evento user_event recibido:', {
          event_type: event.event_type,
          event: event.event,
          agent_id: event.agent_id,
          reason: event.reason,
          timestamp: event.timestamp
        });

        // Activado: Cierre automático de sesión
        this.handleInvalidSession(event.reason, event.event);
      },
      error: (error) => {
        console.error('SessionMonitor: Error en userEvents subscription:', error);
      }
    });

    // Estrategia 1C: Escuchar eventos de cierre de conexión con código 1008
    // El backend cierra la conexión con código 1008 cuando detecta login duplicado
    this.connectionEventSubscription = this.websocketManager.events$.pipe(
      filter((event: ConnectionEvent) => {
        const eventType = String((event as any)?.type || '');
        const code = Number((event as any)?.data?.code);
        const reason = String((event as any)?.data?.reason || '').trim();
        const isCloseEvent = eventType === 'disconnected' || eventType === 'status';
        const isForcedLogout = isCloseEvent && code === 1008 && this.isForcedInvalidationReason(reason);

        if (isCloseEvent) {
          console.warn('SessionMonitor: User WS close event received', {
            code,
            reason,
            decision: isForcedLogout ? 'forced_logout' : 'normal_reconnection'
          });
        }

        if (isForcedLogout) {
          return true;
        }

        const isSessionClosedByServer = event.type === 'status' &&
                                        event.data?.code === 1008 &&
                                        event.data?.reason?.includes('Session invalidated');

        if (isSessionClosedByServer) {
          console.warn('SessionMonitor: Cierre de conexión detectado (código 1008):', {
            code: event.data.code,
            reason: event.data.reason,
            timestamp: event.data.timestamp
          });
        }

        return isSessionClosedByServer;
      })
    ).subscribe({
      next: (event: ConnectionEvent) => {
        console.warn('SessionMonitor: WebSocket cerrado por servidor (sesión invalidada)');
        const reason = String((event as any)?.data?.reason || 'Session invalidated');
        console.warn('SessionMonitor: User WS policy close triggers forced logout', { reason });
        this.handleInvalidSession(reason, 'session_invalidated');
      },
      error: (error) => {
        console.error('SessionMonitor: Error en events subscription:', error);
      }
    });

    console.log('SessionMonitor: Monitoreo WebSocket configurado (messages$ + userEvents$ + events$)');
  }

  /**
   * Configura el monitoreo por polling como fallback
   */
  private setupPollingMonitoring(): void {
    this.pollingSubscription = interval(this.CHECK_INTERVAL).pipe(
      // Solo continuar si hay usuario logueado
      takeWhile(() => this.sharedDataService.isUserLoggedIn()),
      // Validar sesión
      switchMap(() => this.validateSession()),
      // Manejar errores
      catchError(error => {
        console.warn('SessionMonitor: Error al validar sesión (polling):', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          timestamp: new Date().toISOString()
        });

        // Si recibimos 401/403, la sesión fue invalidada
        if (error.status === 401 || error.status === 403) {
          // TEMPORARY: An expired operational authToken must not terminate the
          // session. Keep User WS listeners active until refresh is stabilized.
          console.warn('SessionMonitor: Polling rejected; preserving User WS session monitoring', {
            status: error.status,
            url: error.url,
            timestamp: new Date().toISOString()
          });
          this.pollingEndpointUnavailable = true;
          // TODO: Restore once operational authToken expiration is resolved.
          // this.handleInvalidSession('token_expired', 'polling_validation_failed');
        }

        // El endpoint de polling es un respaldo. Un 404 no debe desuscribir
        // los listeners del User WS que reciben session_invalidated y close 1008.
        if (error.status === 404) {
          this.pollingEndpointUnavailable = true;
          console.warn('SessionMonitor: Polling deshabilitado; User WS sigue monitoreando la sesion');
        }

        // Continuar con el monitoreo (no romper el interval)
        return EMPTY;
      })
    ).subscribe({
      next: () => {
        console.log('SessionMonitor: Sesión válida (polling)');
      },
      error: (error) => {
        console.error('SessionMonitor: Error crítico:', error);
        this.stopMonitoring();
      },
      complete: () => {
        console.log('SessionMonitor: Monitoreo completado (usuario deslogueado)');
        if (this.pollingEndpointUnavailable) {
          return;
        }
        this.stopMonitoring();
      }
    });
  }

  /**
   * Marca que el usuario está haciendo logout manual
   * Esto previene que se muestre el popup de sesión cerrada
   */
  markManualLogout(): void {
    console.log('SessionMonitor: Marcando logout manual - popup deshabilitado');
    this.isManualLogout = true;
    this.stopMonitoring();
  }

  handleOperationalSessionRefreshFailure(error: any): void {
    console.error('SessionMonitor: Operational session refresh failed', {
      status: error?.status ?? null,
      statusText: error?.statusText ?? null,
      body: error?.error ?? null
    });
    this.handleInvalidSession('operational_session_refresh_failed', 'session_invalidated');
  }

  /**
   * Detiene el monitoreo de la sesión
   */
  stopMonitoring(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
      this.websocketSubscription = null;
    }
    if (this.userEventSubscription) {
      this.userEventSubscription.unsubscribe();
      this.userEventSubscription = null;
    }
    if (this.connectionEventSubscription) {
      this.connectionEventSubscription.unsubscribe();
      this.connectionEventSubscription = null;
    }
    this.cancelCountdown();
    this.activeWebSocketSessionId = null;
    this.isMonitoring = false;
    console.log('SessionMonitor: Monitoreo detenido');
  }

  /**
   * Valida la sesión actual con el backend
   * Envía una petición ligera para verificar si el token sigue siendo válido
   */
  private validateSession(): Observable<any> {
    const authToken = localStorage.getItem('authToken');
    const auth_req = localStorage.getItem('auth_req');

    if (!authToken || !auth_req) {
      console.warn('SessionMonitor: No hay tokens, deteniendo monitoreo');
      this.stopMonitoring();
      return EMPTY;
    }

    const headers = new HttpHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${auth_req}`,
      'authToken': authToken
    });

    // Usamos un endpoint HEAD para hacer una validación ligera
    // HEAD no retorna body, solo headers y status code
    // Si el token es inválido, retornará 401
    return this.http.head(`${this.validateUrl}validate`, { headers });
  }

  /**
   * Maneja el caso cuando la sesión es inválida
   * Muestra diálogo con cuenta regresiva y luego cierra sesión
   */
  private handleInvalidSession(reason?: string, eventType?: string): void {
    // IMPORTANTE: Si el logout es manual (iniciado por el usuario), no mostrar popup
    if (this.isManualLogout) {
      console.log('SessionMonitor: Logout manual en progreso - ignorando evento de sesión invalidada');
      return;
    }

    // IMPORTANTE: Detener monitoreo INMEDIATAMENTE al inicio
    // Esto evita que el SessionMonitor detecte su propio cierre de conexión
    // cuando el usuario cierra sesión manualmente
    this.stopMonitoring();

    this.disconnectAllWebSockets();

    // LOGGING MEJORADO: Capturar stack trace para debug
    const callStack = new Error().stack;
    console.warn('SessionMonitor: Sesión invalidada detectada - mostrando diálogo', {
      reason,
      eventType,
      timestamp: new Date().toISOString(),
      callStack: callStack
    });

    // Evitar múltiples ejecuciones
    if (this.logoutCoordinator.getCurrentStatus().isLoggingOut) {
      console.log('SessionMonitor: Logout ya en progreso - evitando duplicación');
      return;
    }

    // Obtener mensaje personalizado
    const notificationMessage = this.getNotificationMessage(reason || 'unknown', eventType || 'session_invalidated');

    // Mostrar diálogo con cuenta regresiva (el logout se ejecuta después)
    if (eventType === 'session_invalidated') {
      this.showSessionInvalidatedDialog(notificationMessage);
      this.performLogout(reason);
      return;
    }

    this.showSessionClosedDialog(notificationMessage);
  }

  private disconnectAllWebSockets(): void {
    this.websocketManager.disconnect();
    this.messageWebsocketService.disconnect();
    this.conversationWebsocketService.disconnect();
    this.stateWebsocketService.disconnect();
  }

  private showSessionInvalidatedDialog(message: string): void {
    this.confirmationService.confirm({
      key: 'sessionClosed',
      header: 'Sesion cerrada',
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'OK',
      rejectVisible: false,
      acceptButtonStyleClass: 'p-button-danger',
      blockScroll: true,
      closeOnEscape: false,
      dismissableMask: false,
      accept: () => undefined
    });
  }

  private showForcedSessionClosedDialog(message: string): void {
    this.confirmationService.confirm({
      key: 'sessionClosed',
      header: 'SesiÃ³n Cerrada',
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'OK',
      rejectVisible: false,
      acceptButtonStyleClass: 'p-button-danger',
      blockScroll: true,
      closeOnEscape: false,
      dismissableMask: false,
      accept: () => undefined
    });
  }

  /**
   * Muestra diálogo de confirmación con cuenta regresiva de 5 segundos
   */
  private showSessionClosedDialog(message: string): void {
    let countdown = 5;

    this.confirmationService.confirm({
      key: 'sessionClosed', // Key para identificar el diálogo
      header: 'Sesión Cerrada',
      message: `${message}\n\nCerrando sesión en ${countdown} segundos...`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'OK',
      rejectVisible: false, // Ocultar botón de cancelar
      acceptButtonStyleClass: 'p-button-danger',
      blockScroll: true,
      closeOnEscape: false,
      dismissableMask: false,
      accept: () => {
        // Usuario hizo clic en "OK"
        this.cancelCountdown();
        this.performLogout();
      }
    });

    // Iniciar cuenta regresiva
    this.countdownSubscription = timer(1000, 1000)
      .pipe(
        tap((elapsed) => {
          countdown = 5 - (elapsed + 1);
          console.log(`⏱️ SessionMonitor: Cuenta regresiva ${countdown}s`);

          if (countdown > 0) {
            // Actualizar mensaje del diálogo
            const updatedMessage = `${message}\n\nCerrando sesión en ${countdown} segundos...`;

            // CRÍTICO: Actualizar el diálogo usando el mismo key
            this.confirmationService.confirm({
              key: 'sessionClosed',
              header: 'Sesión Cerrada',
              message: updatedMessage,
              icon: 'pi pi-exclamation-triangle',
              acceptLabel: 'OK',
              rejectVisible: false,
              acceptButtonStyleClass: 'p-button-danger',
              blockScroll: true,
              closeOnEscape: false,
              dismissableMask: false,
              accept: () => {
                this.cancelCountdown();
                this.performLogout();
              }
            });
          } else {
            // Tiempo agotado - cerrar sesión automáticamente
            console.log('SessionMonitor: Tiempo agotado - cerrando sesión automáticamente');
            this.cancelCountdown();
            this.performLogout();
          }
        })
      )
      .subscribe();
  }

  /**
   * Cancela la cuenta regresiva
   */
  private cancelCountdown(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = null;
    }
  }

  /**
   * Ejecuta el cierre de sesión
   */
  private performLogout(reason?: string): void {
    console.log('SessionMonitor: Ejecutando cierre de sesión');

    // Mostrar notificación final
    this.messageService.add({
      severity: 'warn',
      summary: 'Sesión cerrada',
      detail: reason
        ? this.getNotificationMessage(reason, 'session_invalidated')
        : 'Has sido desconectado',
      life: 3000
    });

    // Limpiar datos locales
    localStorage.removeItem('auth_req');
    localStorage.removeItem('authToken');
    localStorage.removeItem('status_agent');
    localStorage.removeItem('worksel');
    localStorage.removeItem('logged_in_agent');
    this.sharedDataService.clearUser();

    // Actualizar estado de logout
    this.logoutCoordinator.updateLogoutPhase('storage-cleanup');
    this.logoutCoordinator.updateLogoutPhase('redirect');

    // Redirigir a login
    this.router.navigate(['/login'], {
      queryParams: {
        sessionExpired: 'true',
        sessionClosedBySupervisor: reason === 'Session closed by supervisor' ? 'true' : undefined,
        sessionReplacedByNewLogin: this.isSessionReplacementReason(reason) ? 'true' : undefined
      }
    });

    // Completar proceso de logout
    setTimeout(() => {
      this.logoutCoordinator.completeLogout();
    }, 500);
  }

  /**
   * Genera mensaje de notificación personalizado según el reason
   */
  private getNotificationMessage(reason: string, eventType: string): string {
    if (reason === 'Session closed by supervisor') {
      return 'Tu sesion fue cerrada por un supervisor';
    }

    if (this.isSessionReplacementReason(reason)) {
      return 'Tu sesion fue reemplazada por un nuevo inicio de sesion';
    }

    const messages: { [key: string]: string } = {
      'manual_cleanup': 'Tu sesión ha sido cerrada por un administrador',
      'duplicate_login': 'Has iniciado sesión desde otro dispositivo',
      'security_violation': 'Sesión cerrada por razones de seguridad',
      'admin_force_logout': 'Un administrador ha cerrado tu sesión',
      'session_expired': 'Tu sesión ha expirado por inactividad',
      'logout': 'Tu sesión fue cerrada desde otro dispositivo',
      'force_logout': 'Tu sesión fue cerrada forzadamente',
      'connection_closed_by_server': 'El servidor cerró tu sesión (inicio de sesión duplicado detectado)'
    };

    return messages[reason] || messages[eventType] || 'Tu sesión ha sido cerrada';
  }

  /**
   * Verifica si el monitoreo está activo
   */
  private belongsToActiveSession(payload: SessionInvalidatedPayload): boolean {
    const eventSessionId = String(payload.session_id ?? payload.sessionId ?? '').trim();
    const activeSessionId = String(this.activeWebSocketSessionId ?? '').trim();

    // Before the User WS sends its session event, the backend delivery channel
    // remains the source of truth for invalidations targeting the old socket.
    return !eventSessionId || !activeSessionId || eventSessionId === activeSessionId;
  }

  private isForcedInvalidationReason(reason: string): boolean {
    return [
      'Session replaced by new login or resume',
      'Session replaced by new login',
      'Session closed by supervisor',
      'Session invalidated'
    ].includes(reason);
  }

  private isSessionReplacementReason(reason: string | undefined): boolean {
    return reason === 'Session replaced by new login or resume'
      || reason === 'Session replaced by new login';
  }

  private setActiveWebSocketSessionId(payload: SessionInvalidatedPayload): void {
    const sessionId = String(payload.session_id ?? payload.sessionId ?? '').trim();
    if (!sessionId) {
      return;
    }

    this.activeWebSocketSessionId = sessionId;
    console.log('SessionMonitor: User WS session established', { sessionId });
  }

  isActive(): boolean {
    return this.isMonitoring;
  }
}
